import { createContext, useContext, useState, useCallback, type ReactNode } from "react"
import type { Product } from "@/data/products"
import toast from "react-hot-toast"

export interface CartItem {
  product: Product
  size: string
  quantity: number
}

interface CartContextType {
  items: CartItem[]
  addItem: (product: Product, size: string, lang?: "fr" | "en") => void
  removeItem: (productId: string, size: string) => void
  updateQuantity: (productId: string, size: string, quantity: number, lang?: "fr" | "en") => void
  clearCart: () => void
  totalItems: number
  totalPrice: number
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const CartContext = createContext<CartContextType | null>(null)

const limitMessage = (lang: "fr" | "en") =>
  lang === "fr"
    ? "Pour une commande de plus de 3 pochettes, contactez-nous à info@clips-on.ch pour un devis d'expédition."
    : "For orders of more than 3 pouches, contact us at info@clips-on.ch for a shipping quote."

const limitToastStyle = {
  duration: 3000,
  style: {
    background: "#FFFFFF",
    color: "#82391F",
    border: "1px solid rgba(225, 89, 63, 0.2)",
  },
  iconTheme: {
    primary: "#E1593F",
    secondary: "#FFFFFF",
  },
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])
  const [isOpen, setIsOpen] = useState(false)

  const addItem = useCallback((product: Product, size: string, lang: "fr" | "en" = "fr") => {
    if (product.price === 0 || product.soldOut) return

    setItems(prev => {
      const currentTotal = prev.reduce((sum, item) => sum + item.quantity, 0)
      const existing = prev.find(item => item.product.id === product.id && item.size === size)

      if (currentTotal >= 3) {
        toast.error(limitMessage(lang), limitToastStyle)
        return prev
      }

      if (existing) {
        return prev.map(item =>
          item.product.id === product.id && item.size === size
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }
      return [...prev, { product, size, quantity: 1 }]
    })
    setIsOpen(true)
  }, [])

  const removeItem = useCallback((productId: string, size: string) => {
    setItems(prev => prev.filter(item => !(item.product.id === productId && item.size === size)))
  }, [])

  const updateQuantity = useCallback((productId: string, size: string, quantity: number, lang: "fr" | "en" = "fr") => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => !(item.product.id === productId && item.size === size)))
      return
    }
    setItems(prev => {
      const otherItemsTotal = prev
        .filter(item => !(item.product.id === productId && item.size === size))
        .reduce((sum, item) => sum + item.quantity, 0)

      if (otherItemsTotal + quantity > 3) {
        toast.error(limitMessage(lang), limitToastStyle)
        return prev
      }

      return prev.map(item =>
        item.product.id === productId && item.size === size
          ? { ...item, quantity }
          : item
      )
    })
  }, [])

  const clearCart = useCallback(() => setItems([]), [])

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalPrice = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0)

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice, isOpen, setIsOpen }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) throw new Error("useCart must be used within CartProvider")
  return context
}
